import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {FormattedMessage, useIntl} from 'react-intl';

import {IStore} from 'redux/interface';
import {
  createSearchDialogue,
  getSearchDialogues,
  getTranslations,
  removeSearchDialogue,
  searchAsyncActions
} from 'redux/pageAsyncActions';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {TranslationKeys} from 'utils/constants/routeConstants';

import {ReactComponent as ChatIcon} from 'assets/image/menu-auth/chat.svg';
import {ButtonVariant, GritxButton} from '@wholesalechange/chatcomponent';
import NotFoundInfo from '../../components/not-found-info';
import NotFoundImg from 'assets/image/common/notFound.png';
import Loader from '../../components/loader';
import {useNavigate} from 'react-router-dom';
import {IRadioInput} from '../../components/input-radio/interfaces';
import InputRadio from '../../components/input-radio';

import './styles.scss';
import {clearSearchDialogue} from '../../redux/search/actions';
import {ISearchDialogue} from '../../redux/search/interfaces';
import SearchCardSection from '../../components/search-card-section';

export const Search: React.FunctionComponent = () => {
  const {
    searchDialogues,
    searchDialogue
  } = useSelector((state: IStore) => state.search);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sorted, setSorted] = useState<ISearchDialogue[]>([]);
  const [sort, setSort] = useState<string>('new');
  const [afterCreate, setAfterCreate] = useState<boolean>(false);

  useEffect(() => {
    dispatch(clearSearchDialogue());
  }, []);

  const sortRadioInputs: IRadioInput[] = [
    {
      id: 0,
      name: 'new',
      label: 'Newest',
      value: 'new'
    },
    {
      id: 1,
      name: 'old',
      label: 'Oldest',
      value: 'old'
    }];

  function getTime(date?: Date) {
    return date != null ? new Date(date).getTime() : 0;
  }

  function getSortedDialogues(sortMethod: string) {
    const dialoguesCopy = searchDialogues.slice(0);

    if (sortMethod === 'old') {
      return dialoguesCopy.sort((a, b) => getTime(a.dialogUserDate) - getTime(b.dialogUserDate));
    }

    return dialoguesCopy.sort((a, b) => getTime(b.dialogUserDate) - getTime(a.dialogUserDate));
  }

  useEffect(() => {
    if (searchDialogues) {
      setSorted(getSortedDialogues(sort));
    }
  }, [searchDialogues, sort]);

  useGetTranslations({
    translationKey: TranslationKeys.Search
  });

  useEffect(() => {
    if (afterCreate && searchDialogue) {
      navigate(`/search/${searchDialogue.dialogId}`);
      setAfterCreate(false);
    }
  }, [searchDialogue]);

  useEffect(() => {
    dispatch(searchAsyncActions[getSearchDialogues]());
  }, []);

  function handleOpenDialogue(dialogue: ISearchDialogue) {
    navigate(`/search/${dialogue.dialogId}`);
  }

  function onCreateSearchDialogue() {
    const date = new Date();
    const dateStr = intl.formatDate(date, {year: 'numeric',
      month: 'long',
      hour: 'numeric',
      minute: 'numeric',
      day: '2-digit'});

    dispatch(searchAsyncActions[createSearchDialogue]({
      dialogId: 0,
      dialogName: `Search of ${dateStr}`,
      dialogUserDate: date,
      percentage: 0
    }));
    setAfterCreate(true);
  }

  function handleChangeSort(value: string) {
    setSort(value);
  }

  function renderDialogueCards() {
    return <div className="search__cards">
      {sorted.length === 0 ? <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.search.sectionCards.notFound',
          defaultMessage: 'Sorry, but nothing can be found.'
        })}
        image={NotFoundImg}
      /> : <SearchCardSection
        showLoader={loading.includes(getSearchDialogues)}
        cards={sorted}
        onClickCard={handleOpenDialogue}
      />}
    </div>;
  }

  return <section className={'search container'}>
    {
      !loading.includes(getTranslations) && <>
        <h1 className="search__title">
          <ChatIcon className="search__icon"/>
          <FormattedMessage
            id={'gritx.search.title'}
            defaultMessage={'Search'}
          />
        </h1>
        <div className="search__header">
          <div className="search__header-sort">
            <InputRadio inputs={sortRadioInputs} value={sort} onChange={handleChangeSort} label={intl.formatMessage({
              id: 'gritx.search.sort.title',
              defaultMessage: 'Sort by:'
            })}/>
          </div>
          <GritxButton
            title={intl.formatMessage({
              id: 'gritx.search.create',
              defaultMessage: 'Create'
            })}
            className={'search__button'}
            variant={ButtonVariant.Primary}
            onClick={onCreateSearchDialogue}
          />
        </div>
        {
          loading.includes(getSearchDialogues) || loading.includes(removeSearchDialogue)
          || loading.includes(createSearchDialogue) ? <Loader/> : renderDialogueCards()
        }
      </>
    }
  </section>;
};
